import React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';

interface ICreditApplicationControlsProps {
  applicationDate: Date;
  setApplicationDate: (date: Date) => void;
  isSubmitting: boolean;
  hasAnyInvoiceApplications: boolean;
  onRemoveAllInvoices: () => void;
  onAddAllSourceInvoices: () => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  submitDisabled?: boolean;
  showOnlyActions?: boolean;
  error?: string | null;
  validationError?: string | null;
}

export const CreditApplicationControls: React.FC<ICreditApplicationControlsProps> = ({
  applicationDate,
  setApplicationDate,
  isSubmitting,
  hasAnyInvoiceApplications,
  onRemoveAllInvoices,
  onAddAllSourceInvoices,
  onCancel,
  onSubmit,
  submitDisabled = false,
  showOnlyActions = false,
  error = null,
  validationError = null,
}) => {
  // If showing only actions (for dialog footer), render action buttons
  if (showOnlyActions) {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        {onCancel && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
        )}
        {onSubmit && (
          <Button
            variant="contained"
            color="success"
            onClick={onSubmit}
            disabled={submitDisabled || isSubmitting || !!error || !!validationError}
            {...(!isSubmitting && { startIcon: <CheckIcon /> })}
          >
            {isSubmitting ? 'Applying...' : 'Apply Credit Notes'}
          </Button>
        )}
      </Box>
    );
  }

  // Regular form controls
  return (
    <Box sx={{ mb: 3, mt: 2}}>
      {/* Date picker section */}
      <Box sx={{ mb: 2 }}>
        <DatePicker
          label="Application Date"
          value={applicationDate}
          onChange={(date) => setApplicationDate(date ?? new Date())}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Box>

      {/* Control buttons section */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={onRemoveAllInvoices}
          disabled={isSubmitting || !hasAnyInvoiceApplications}
          title="Remove all invoice applications from all credit notes"
          startIcon={<DeleteIcon />}
        >
          Remove All Invoices
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={onAddAllSourceInvoices}
          disabled={isSubmitting}
          title="Re-add all original invoices from the credit note sources with default amounts"
          startIcon={<AssignmentIcon />}
        >
          Add All Source Invoices
        </Button>
      </Box>

      {/* Section header */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Credit Notes & Invoice Applications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select and configure invoice applications for each credit note
        </Typography>
      </Box>
    </Box>
  );
};

export default CreditApplicationControls;
